import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Descriptor for the first-class EMPTY state of a tab section.
 * The icon is a neutral heroicons (24/outline) glyph; heading/body name the
 * absent concept honestly (e.g. "No marks recorded yet").
 */
export interface TabEmptyDescriptor {
  icon: React.ComponentType<{ className?: string }>;
  heading: string;
  body: string;
}

export interface TabStateProps {
  /** True while the backing query is loading. */
  isLoading: boolean;
  /** True when the backing query errored (fetch failure only — never "no data"). */
  isError: boolean;
  /** True when the query resolved with no records (explicit emptiness, not an error). */
  isEmpty: boolean;
  /** Wired to `query.refetch()` by consumers; rendered as the "Try again" affordance. */
  onRetry?: () => void;
  /** Accessible label for the loading `role="status"` region. Default "Loading". */
  loadingLabel?: string;
  /** Names the section in the error heading: "Couldn't load {errorSection}". */
  errorSection?: string;
  /** First-class empty-state descriptor (icon + honest heading + body). */
  empty: TabEmptyDescriptor;
  /** REAL content — rendered only when not loading, not error, not empty. */
  children: React.ReactNode;
}

/**
 * Reusable three-state (four-branch) wrapper for trade-detail tab bodies.
 *
 * Renders EXACTLY ONE of loading / error / empty / real, with the binding
 * precedence (08-UI-SPEC "Three-State Contract"):
 *
 *   if (isLoading)      -> LOADING
 *   else if (isError)   -> ERROR
 *   else if (isEmpty)   -> EMPTY   // first-class, never a fabricated fallback
 *   else                -> children (REAL)
 *
 * Empty is neutral (gray icon, no red/amber) and distinct from error. This is
 * the single shared seam consumed by Phases 10-12 — do not re-implement the
 * loading/error/empty markup in individual tabs.
 */
export function TabState({
  isLoading,
  isError,
  isEmpty,
  onRetry,
  loadingLabel = 'Loading',
  errorSection = 'this data',
  empty,
  children,
}: TabStateProps) {
  if (isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12"
        role="status"
        aria-live="polite"
      >
        <div
          className="h-8 w-8 motion-safe:animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
          aria-hidden="true"
        />
        <span className="sr-only">{loadingLabel}</span>
        <p className="mt-2 text-sm text-gray-500" aria-hidden="true">
          Loading…
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-red-400 mb-3" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-gray-900">Couldn&apos;t load {errorSection}</h3>
        <p className="mt-1 text-sm text-gray-500">Something went wrong fetching this data.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (isEmpty) {
    const EmptyIcon = empty.icon;
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <EmptyIcon className="h-8 w-8 text-gray-300 mb-3" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-gray-900">{empty.heading}</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-sm">{empty.body}</p>
      </div>
    );
  }

  return <>{children}</>;
}
